import { motion, AnimatePresence } from "motion/react";
import { AlertCircle, CheckCircle, X } from "lucide-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";

export type ToastType = "error" | "success" | "info";

interface GlassToastProps {
  message: string | null;
  type?: ToastType;
  title?: string;
  onClose: () => void;
  duration?: number;
}

export default function GlassToast({
  message,
  type = "error",
  title,
  onClose,
  duration = 5000,
}: GlassToastProps) {
  useEffect(() => {
    if (message) {
      const timer = window.setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [message, duration, onClose]);

  // Determine styles based on type
  const isError = type === "error";
  const isSuccess = type === "success";

  const borderColor = isError ? "border-[#ef5350]/30" : isSuccess ? "border-[#3cb44f]/30" : "border-blue-400/30";
  const bgColor = isError ? "bg-[#ef5350]/10" : isSuccess ? "bg-[#3cb44f]/10" : "bg-blue-400/10";
  const shadowColor = isError ? "shadow-[0_20px_60px_-15px_rgba(239,83,80,0.3)]" : isSuccess ? "shadow-[0_20px_60px_-15px_rgba(60,180,79,0.3)]" : "shadow-[0_20px_60px_-15px_rgba(96,165,250,0.3)]";
  const iconColor = isError ? "text-[#ef5350]" : isSuccess ? "text-[#3cb44f]" : "text-blue-400";
  const iconBg = isError ? "bg-[#ef5350]/20" : isSuccess ? "bg-[#3cb44f]/20" : "bg-blue-400/20";

  // No default title for error/info toasts — only "Success" gets one. Error
  // toasts here cover everything from real auth failures to plain form
  // validation ("enter an amount greater than zero"), and defaulting all of
  // them to "Access Denied" mislabeled ordinary validation messages as
  // permission errors. Callers that actually need a title (e.g. real access
  // errors) can still pass one explicitly via the `title` prop.
  const defaultTitle = isSuccess ? "Success" : undefined;
  const displayTitle = title || defaultTitle;

  // Portaled straight to <body> — several callers (e.g. ContractsOverlay)
  // render this from inside a framer-motion element with an active
  // transform, which creates its own CSS stacking context. z-[9999] can't
  // escape that context no matter how high the number is, so the toast was
  // rendering underneath the fixed Navbar instead of above it. Portaling
  // sidesteps the whole ancestor-stacking-context problem.
  return createPortal(
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          className={`group fixed top-8 right-8 z-[9999] w-[min(480px,calc(100vw-4rem))] rounded-2xl border ${borderColor} ${bgColor} px-5 py-4 backdrop-blur-xl ${shadowColor} flex items-start gap-4`}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Dismiss"
            className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-[#2a2a2a] border border-white/15 shadow-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-[#3a3a3a] transition-all duration-150 opacity-0 scale-75 pointer-events-none group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto cursor-pointer"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2.5} />
          </button>
          <div className={`w-8 h-8 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
            {isError ? (
              <AlertCircle className={`w-4 h-4 ${iconColor}`} />
            ) : (
              <CheckCircle className={`w-4 h-4 ${iconColor}`} />
            )}
          </div>
          <div className={`flex flex-col gap-1 ${displayTitle ? '' : 'justify-center self-center'}`}>
            {displayTitle && (
              <span className="text-sm font-bold text-white tracking-tight">{displayTitle}</span>
            )}
            <span className="text-[13px] text-white/80 leading-relaxed font-medium whitespace-pre-line">
              {message}
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
